import { forwardRef, type SVGProps } from 'react';
import {
  Activity,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  ArrowUpRight,
  AtSign,
  Barcode,
  Bell,
  Blocks,
  Bookmark,
  Bot,
  Box,
  Boxes,
  Braces,
  Briefcase,
  Building2,
  Calendar,
  ChartColumn,
  ChartPie,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  CircleAlert,
  CircleCheck,
  CircleDot,
  CircleHelp,
  CircleUser,
  CircleX,
  Clipboard,
  ClipboardList,
  Clock,
  CloudOff,
  Code,
  Coins,
  Columns3,
  Command,
  Container,
  Copy,
  CreditCard,
  Database,
  Dot,
  Download,
  Ellipsis,
  EllipsisVertical,
  Equal,
  ExternalLink,
  Eye,
  EyeOff,
  File,
  FileText,
  Filter,
  Flag,
  FlaskConical,
  Folder,
  Forklift,
  Gauge,
  GitBranch,
  Globe,
  GripVertical,
  Handshake,
  Hash,
  History,
  House,
  Inbox,
  Info,
  Kanban,
  KeyRound,
  Keyboard,
  Languages,
  LayoutDashboard,
  LayoutGrid,
  Layers,
  Link,
  List,
  ListChecks,
  LoaderCircle,
  Lock,
  LockOpen,
  LogIn,
  LogOut,
  Mail,
  Map,
  MapPin,
  Maximize2,
  Menu,
  MessageSquare,
  Minimize2,
  Minus,
  Monitor,
  Moon,
  Move,
  Package,
  PackageCheck,
  PackageX,
  PanelLeft,
  PanelRight,
  Paperclip,
  Pause,
  Pencil,
  Percent,
  Phone,
  Pin,
  Plane,
  Play,
  Plug,
  Plus,
  Printer,
  Radar,
  Receipt,
  Redo2,
  RefreshCw,
  Route,
  Ruler,
  Save,
  ScanLine,
  Search,
  Send,
  Settings,
  Shield,
  Ship,
  Sparkles,
  Square,
  Star,
  StarOff,
  Sun,
  Table2,
  Tag,
  Tags,
  Target,
  Thermometer,
  Timer,
  TrainFront,
  Trash2,
  TriangleAlert,
  Truck,
  Undo2,
  Unlink,
  Upload,
  User,
  Users,
  Wallet,
  Warehouse,
  Weight,
  Workflow,
  Wrench,
  X,
  Zap,
  ZoomIn,
  ZoomOut,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../utils/cn';

/**
 * Icon registry: kebab-case name → Lucide component.
 *
 * Every name the codebase used with the previous hand-drawn set is kept (mapped to the closest Lucide
 * glyph), alongside the canonical domain vocabulary from the UI rebuild brief. Add a name here to make
 * it available to `<Icon name="…" />` and to `IconName`.
 */
const components = {
  /* ── Actions & navigation ────────────────────────────────────────────── */
  check: Check,
  x: X,
  plus: Plus,
  minus: Minus,
  equal: Equal,
  percent: Percent,
  dot: Dot,
  'circle-dot': CircleDot,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevrons-left': ChevronsLeft,
  'chevrons-right': ChevronsRight,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up-right': ArrowUpRight,
  search: Search,
  filter: Filter,
  sort: ArrowUpDown,
  menu: Menu,
  'more-horizontal': Ellipsis,
  'more-vertical': EllipsisVertical,
  settings: Settings,
  home: House,
  'help-circle': CircleHelp,
  'circle-help': CircleHelp,
  'undo-2': Undo2,
  'redo-2': Redo2,
  refresh: RefreshCw,
  'refresh-cw': RefreshCw,
  download: Download,
  upload: Upload,
  printer: Printer,
  external: ExternalLink,
  'external-link': ExternalLink,
  link: Link,
  unlink: Unlink,
  copy: Copy,
  save: Save,
  pencil: Pencil,
  trash: Trash2,
  'trash-2': Trash2,
  eye: Eye,
  'eye-off': EyeOff,
  lock: Lock,
  unlock: LockOpen,
  'zoom-in': ZoomIn,
  'zoom-out': ZoomOut,
  move: Move,
  drag: GripVertical,
  'grip-vertical': GripVertical,
  maximize: Maximize2,
  'maximize-2': Maximize2,
  minimize: Minimize2,
  'minimize-2': Minimize2,
  play: Play,
  pause: Pause,
  square: Square,
  send: Send,
  logout: LogOut,
  'log-out': LogOut,
  'log-in': LogIn,
  command: Command,
  keyboard: Keyboard,
  spinner: LoaderCircle,

  /* ── Status & feedback ───────────────────────────────────────────────── */
  alert: TriangleAlert,
  'alert-triangle': TriangleAlert,
  'triangle-alert': TriangleAlert,
  'alert-circle': CircleAlert,
  'circle-alert': CircleAlert,
  info: Info,
  'check-circle': CircleCheck,
  'circle-check': CircleCheck,
  'x-circle': CircleX,
  'circle-x': CircleX,
  zap: Zap,
  bell: Bell,
  flag: Flag,
  star: Star,
  'star-off': StarOff,
  bookmark: Bookmark,
  pin: Pin,
  target: Target,
  activity: Activity,
  'cloud-off': CloudOff,

  /* ── Views & layout ──────────────────────────────────────────────────── */
  grid: LayoutGrid,
  'layout-grid': LayoutGrid,
  list: List,
  'list-checks': ListChecks,
  'table-2': Table2,
  kanban: Kanban,
  columns: Columns3,
  'columns-3': Columns3,
  layout: LayoutDashboard,
  'panel-left': PanelLeft,
  'panel-right': PanelRight,
  sun: Sun,
  moon: Moon,
  monitor: Monitor,

  /* ── People & access ─────────────────────────────────────────────────── */
  user: User,
  'circle-user': CircleUser,
  users: Users,
  shield: Shield,
  key: KeyRound,
  'key-round': KeyRound,
  briefcase: Briefcase,
  handshake: Handshake,
  building: Building2,
  'building-2': Building2,
  mail: Mail,
  phone: Phone,
  'at-sign': AtSign,

  /* ── Logistics domain ────────────────────────────────────────────────── */
  truck: Truck,
  ship: Ship,
  plane: Plane,
  train: TrainFront,
  'train-front': TrainFront,
  route: Route,
  map: Map,
  'map-pin': MapPin,
  warehouse: Warehouse,
  dock: Container,
  container: Container,
  forklift: Forklift,
  package: Package,
  'package-check': PackageCheck,
  'package-x': PackageX,
  box: Box,
  boxes: Boxes,
  layers: Layers,
  inbox: Inbox,
  clipboard: Clipboard,
  'clipboard-list': ClipboardList,
  barcode: Barcode,
  scan: ScanLine,
  'scan-line': ScanLine,
  tag: Tag,
  tags: Tags,
  hash: Hash,
  gauge: Gauge,
  timer: Timer,
  thermometer: Thermometer,
  weight: Weight,
  ruler: Ruler,
  radar: Radar,
  calendar: Calendar,
  clock: Clock,
  history: History,

  /* ── Finance ─────────────────────────────────────────────────────────── */
  coins: Coins,
  receipt: Receipt,
  'credit-card': CreditCard,
  wallet: Wallet,

  /* ── Data, files & Studio ────────────────────────────────────────────── */
  'bar-chart': ChartColumn,
  'bar-chart-3': ChartColumn,
  'pie-chart': ChartPie,
  file: File,
  'file-text': FileText,
  folder: Folder,
  paperclip: Paperclip,
  'message-square': MessageSquare,
  database: Database,
  plug: Plug,
  globe: Globe,
  languages: Languages,
  code: Code,
  braces: Braces,
  'git-branch': GitBranch,
  workflow: Workflow,
  blocks: Blocks,
  'flask-conical': FlaskConical,
  wrench: Wrench,
  sparkles: Sparkles,
  robot: Bot,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof components;

export const iconComponents: Record<IconName, LucideIcon> = components;

export const iconNames = Object.keys(components) as IconName[];

/** Sizes the library lays out for; any other number is accepted for one-off cases (e.g. empty-state art). */
export type IconSize = 16 | 20 | 24 | (number & Record<never, never>);

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Rendered width and height in px. Defaults to 16. */
  size?: IconSize;
  /** Provide when the icon carries meaning; otherwise it is aria-hidden. */
  title?: string;
}

const STROKE_WIDTH = 1.75;

export function isIconName(value: unknown): value is IconName {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(components, value);
}

/**
 * Lucide-backed icon. Stroke 1.75 on the 24px grid, `currentColor`, scaling stroke (not absolute), so an
 * icon reads the same weight at 16, 20 and 24. Unknown names (only possible through untyped data such as
 * metadata) render nothing and warn in development instead of throwing.
 */
export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon({ name, size = 16, title, className, ...rest }, ref) {
  if (!isIconName(name)) {
    if (import.meta.env.DEV) console.warn(`[design-system] <Icon name="${String(name)}"> is not a registered icon.`);
    return null;
  }
  const Component = iconComponents[name];
  return (
    <Component
      ref={ref}
      size={size}
      strokeWidth={STROKE_WIDTH}
      stroke="currentColor"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      className={cn('zo-icon shrink-0', className)}
      data-icon={name}
      {...rest}
    >
      {title ? <title key="title">{title}</title> : null}
    </Component>
  );
});
